import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CORPORATE } from '../constants/candidateFormStyles';
import { MODAL_FILTER_MENU_Z_INDEX } from '../constants/filterBar';

function optionText(opt) {
  const raw = opt?.label ?? opt?.value ?? '';
  return raw == null ? '' : String(raw);
}

/**
 * Corporate multi-select for filter modals — portaled menu, rounded UI, search.
 */
export default function CorporateMultiSelect({
  options = [],
  values = [],
  onChange,
  emptyLabel = 'Any',
  searchable = true,
  menuMinWidth = 240,
  menuZIndex = MODAL_FILTER_MENU_Z_INDEX,
  profileFieldStyle = false
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuMinWidth });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...options].sort((a, b) =>
      optionText(a).localeCompare(optionText(b), undefined, { sensitivity: 'base' })
    );
    if (!q) return list;
    return list.filter(
      o => optionText(o).toLowerCase().includes(q) || String(o.value || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const buttonLabel = useMemo(() => {
    if (!values.length) return emptyLabel;
    if (values.length === 1) {
      const opt = options.find(o => o.value === values[0]);
      return opt?.label || values[0];
    }
    return `${values.length} selected`;
  }, [values, options, emptyLabel]);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuW = Math.max(menuMinWidth, rect.width, menuRef.current?.offsetWidth || 0);
    const menuH = Math.min(menuRef.current?.offsetHeight || 280, 280);
    const gap = 6;
    const pad = 8;
    let left = rect.left;
    if (left + menuW > window.innerWidth - pad) left = Math.max(pad, rect.right - menuW);
    let top = rect.bottom + gap;
    if (top + menuH > window.innerHeight - pad) {
      const above = rect.top - menuH - gap;
      if (above >= pad) top = above;
    }
    setPos({ top, left, width: menuW });
  }, [menuMinWidth]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    const onPointerDown = e => {
      if (!triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, updatePosition, filteredOptions.length, values.length, query]);

  const toggleValue = val => {
    if (selectedSet.has(val)) onChange(values.filter(v => v !== val));
    else onChange([...values, val]);
  };

  const hasSelection = values.length > 0;

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        aria-multiselectable="true"
        className={`fixed ${CORPORATE.menu}`}
        style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: menuZIndex }}
        onMouseDown={e => e.preventDefault()}
      >
        {searchable && (
          <div className="p-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]">
            <input
              type="text"
              className="w-full border border-[hsl(var(--ds-border))] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[hsl(var(--ds-border))] text-xs bg-[hsl(var(--ds-surface))]">
          <button
            type="button"
            className="text-[hsl(var(--ds-primary))] hover:underline font-medium"
            onClick={() => onChange([...new Set([...values, ...filteredOptions.map(o => o.value)])])}
          >
            Select visible
          </button>
          <button type="button" className="text-[hsl(var(--ds-muted-fg))] hover:underline" onClick={() => onChange([])}>
            Clear
          </button>
        </div>
        <ul className="max-h-52 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-[hsl(var(--ds-muted-fg))] text-xs">No options</li>
          ) : (
            filteredOptions.map(opt => {
              const checked = selectedSet.has(opt.value);
              return (
                <li key={opt.value}>
                  <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--ds-muted))] cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] focus:ring-[hsl(var(--ds-ring))] h-4 w-4"
                      checked={checked}
                      onChange={() => toggleValue(opt.value)}
                    />
                    <span className={`text-sm ${checked ? 'font-medium text-[hsl(var(--ds-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      {opt.label || opt.value}
                    </span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className="relative min-w-0 w-full">
      <button
        ref={triggerRef}
        type="button"
        className={`w-full text-left text-sm border bg-[hsl(var(--ds-surface))] shadow-sm transition-all flex items-center justify-between gap-2 ${
          profileFieldStyle
            ? 'qualification-multiselect-trigger rounded-lg'
            : 'min-h-[2.5rem] px-3 py-2 rounded-xl'
        } ${
          hasSelection
            ? 'border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] ring-1 ring-[hsl(var(--ds-ring))]'
            : 'border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:border-[hsl(var(--ds-border))]'
        } ${open ? 'border-[hsl(var(--ds-primary))] ring-2 ring-[hsl(var(--ds-ring))]' : ''} ${profileFieldStyle ? 'px-2.5' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="truncate flex-1">{buttonLabel}</span>
        <span className="text-[hsl(var(--ds-muted-fg))] text-xs shrink-0">{open ? '▴' : '▾'}</span>
      </button>
      {menu}
    </div>
  );
}
